import React, { useEffect, useState } from 'react';
import { X, Sparkles, BookOpen, ChevronDown, ExternalLink } from 'lucide-react';
import { toSlug } from '../../utils/slug';
import MemberAvatar from './MemberAvatar';
import { useCatalogAvatars } from './useCatalogAvatars';
import AgentKnowledgeBase from './AgentKnowledgeBase';
import AgentStatCard from './AgentStatCard';
import { flowOverlay, flowPanel, modalHeader, modalTitle, modalLabel, modalField } from '../ui/modalSpacing';
import { updateConversationMember, type ConversationSummary } from '../../services/conversationService';
import { getAgentBio, type AgentBio } from '../../services/agentService';
import AgentGallery, { GalleryStrip } from './AgentGallery';
import { getAgentGallery, type AgentGalleryView } from '../../services/galleryService';
import { useIsDesktop } from '../../hooks/useMediaQuery';
import { useIsAdmin } from '../../contexts/AuthContext';
import type { ChatSession, MemberSpec } from './types';
import { TYPE } from '../../constants/typeScale';

/** One labelled characteristic attribute in the bio card (personality / appearance / style). */
const BioRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className={`flex gap-4 ${TYPE.control} leading-relaxed`}>
    <span className="w-24 shrink-0 uppercase tracking-wide text-secondary-400">{label}</span>
    <span className="flex-1 text-secondary-600 dark:text-secondary-300">{value}</span>
  </div>
);

interface Props {
  open: boolean;
  session: ChatSession | null;
  member: MemberSpec | null;
  isOwner: boolean;
  onClose: () => void;
  onUpdated: (c: ConversationSummary) => void;
  /** Called after a successful spend so the parent can refresh the wallet balance. */
  onWalletChanged?: () => void;
}

/**
 * Agent detail — opened from a Manage-group tile or by tapping an agent's avatar in chat.
 * The owner can set a group-specific display name (alias), per-chat context, generate an
 * avatar (charges credits), and — for their own custom agents — attach a knowledge base
 * (links + documents) so the agent is specialised. Everyone else sees a read-only profile.
 */
const AgentDetailPanel: React.FC<Props> = ({ open, session, member, isOwner, onClose, onUpdated, onWalletChanged }) => {
  const isAdmin = useIsAdmin();
  const catalogAvatars = useCatalogAvatars(); // admin default avatars, keyed by profile_id
  const [alias, setAlias] = useState('');
  const [ctx, setCtx] = useState('');
  const [busy, setBusy] = useState<'' | 'alias' | 'context'>('');
  const [error, setError] = useState('');
  const [lightbox, setLightbox] = useState<string | null>(null);
  const [bio, setBio] = useState<AgentBio | null>(null);
  const [gallery, setGallery] = useState<AgentGalleryView | null>(null);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [galleryTab, setGalleryTab] = useState<'feed' | 'yours'>('feed');
  const [expanded, setExpanded] = useState(false); // "More about" in-place reveal
  const isDesktop = useIsDesktop();

  useEffect(() => {
    if (!open || !member) return;
    setAlias(member.alias || ''); setCtx(member.context || ''); setError(''); setBusy(''); setLightbox(null);
    setBio(null); setGallery(null); setGalleryOpen(false); setGalleryTab('feed'); setExpanded(false);
    let alive = true;
    // Load the agent's BIO (catalog agents only; custom agents return null) + its social gallery. Rapport
    // (Link) is tracked server-side for personalization/proactive check-ins but is no longer shown here.
    const agentId = member.profile_id || member.custom_agent_id;
    if (member.profile_id) getAgentBio(member.profile_id).then((b) => { if (alive) setBio(b); }).catch(() => undefined);
    if (agentId) getAgentGallery(agentId).then((g) => { if (alive) setGallery(g); }).catch(() => undefined);
    return () => { alive = false; };
  }, [open, member]);

  if (!open || !session || !member) return null;
  const displayName = member.alias || member.name;

  const saveAlias = async () => {
    setBusy('alias'); setError('');
    try {
      const next = alias.trim();
      if (next === (member.alias || '')) return onClose();
      onUpdated(await updateConversationMember(session.id, member.id, { alias: next || null }));
      onClose();
    } catch (e: any) { setError(e?.message || 'Could not save the name'); } finally { setBusy(''); }
  };

  const saveContext = async () => {
    setBusy('context'); setError('');
    try {
      const next = ctx.trim();
      if (next === (member.context || '')) { setBusy(''); return; }
      onUpdated(await updateConversationMember(session.id, member.id, { context: next || null }));
    } catch (e: any) { setError(e?.message || 'Could not save the context'); } finally { setBusy(''); }
  };

  const openGallery = (tab: 'feed' | 'yours') => { setGalleryTab(tab); setGalleryOpen(true); };

  return (
    <div className={`${flowOverlay} z-[60]`} onClick={onClose}>
      <div className={`${flowPanel} sm:w-full sm:max-w-sm sm:h-auto sm:max-h-[88dvh]`} onClick={(e) => e.stopPropagation()}>
        <div className={modalHeader}>
          <h2 className={modalTitle}>Agent details</h2>
          <button onClick={onClose} aria-label="Close" title="Close" className="text-secondary-400 hover:text-secondary-700 dark:hover:text-white"><X className="h-4 w-4" /></button>
        </div>

        <div className="overflow-y-auto px-6 py-6 space-y-6">
          <div className="flex flex-col items-center text-center gap-2">
            <MemberAvatar avatarIcon={member.avatar_icon} avatarPath={member.avatar_path} avatarDriveFileId={member.avatar_drive_file_id} avatarImage={member.profile_id ? catalogAvatars[member.profile_id] : null} conversationId={session.id} name={displayName} className="w-20 h-20" onImageClick={setLightbox} />
            <div>
              <p className={`${TYPE.stageTitle} text-secondary-900 dark:text-white`}>
                {displayName}
                {bio?.native_name && <span className="ml-1.5 font-normal text-secondary-400">· {bio.native_name}</span>}
                {member.profile_id && (
                  <a href={`/agent/${toSlug(member.name)}`} target="_blank" rel="noopener noreferrer"
                    title={`Open ${member.name}'s profile`} className="ml-1.5 inline-flex align-middle text-secondary-400 hover:text-secondary-700 dark:hover:text-white">
                    <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                )}
              </p>
              <p className={`${TYPE.control} text-secondary-400`}>{member.persona}</p>
            </div>
            {/* Social gallery entry + a way to make a fresh PRIVATE post (opens the gallery's "Yours" tab). */}
            <div className="mt-1 flex flex-wrap items-center justify-center gap-2">
              {gallery && gallery.posts.length > 0 && (
                <GalleryStrip posts={gallery.posts} onOpen={() => openGallery('feed')} />
              )}
              {member.profile_id && (
                <button onClick={() => openGallery('yours')}
                  className={`inline-flex items-center gap-1.5 rounded-xl border border-secondary-200 dark:border-secondary-600 px-2.5 py-2 ${TYPE.control} font-medium text-secondary-600 dark:text-secondary-300 hover:border-secondary-400 hover:bg-secondary-50 dark:hover:bg-secondary-800/50`}>
                  <Sparkles className="w-3.5 h-3.5" /> Create a post
                </button>
              )}
            </div>
          </div>

          {/* BIO — a SIMPLE, high-level overview for everyone: description, a plain-language personality
              line, and what the agent is good at. The granular character sheet (MBTI + trait/knowledge
              meters) that powers our personality diversification is INTERNAL — admin-only, below. */}
          {bio && (
            <section className="space-y-4">
              {bio.description && <p className={`${TYPE.body} text-secondary-600 dark:text-secondary-300 text-center leading-relaxed`}>{bio.description}</p>}
              {bio.personality && (
                <div className="pt-3 border-t border-secondary-100 dark:border-secondary-600">
                  <BioRow label="Personality" value={bio.personality} />
                </div>
              )}
              {bio.capabilities.length > 0 && (
                <div className="pt-3 border-t border-secondary-100 dark:border-secondary-600">
                  <p className={`mb-2 ${TYPE.label} text-secondary-400`}>Good at</p>
                  <div className="flex flex-wrap gap-1.5">
                    {bio.capabilities.map((c) => (
                      <span key={c} className={`rounded-full border border-secondary-200 dark:border-secondary-600 px-2 py-0.5 ${TYPE.meta} text-secondary-600 dark:text-secondary-200`}>{c}</span>
                    ))}
                  </div>
                </div>
              )}

              {/* More about {name} — expands extra lore IN PLACE (no new tab). */}
              {bio.hobby && (
                <div className="pt-3 border-t border-secondary-100 dark:border-secondary-600">
                  <BioRow label="Hobby" value={bio.hobby} />
                </div>
              )}
              {(bio.backstory || bio.appearance || bio.style) && (
                <div className="pt-1">
                  <button onClick={() => setExpanded((v) => !v)} aria-expanded={expanded}
                    className={`inline-flex items-center gap-1 ${TYPE.control} font-medium text-secondary-500 hover:text-secondary-900 dark:text-secondary-300 dark:hover:text-white`}>
                    More about {displayName}
                    <ChevronDown className={`w-3.5 h-3.5 transition-transform ${expanded ? 'rotate-180' : ''}`} />
                  </button>
                  {expanded && (
                    <div className="mt-3 space-y-3">
                      {bio.backstory && <p className={`${TYPE.body} italic text-secondary-500 dark:text-secondary-400 leading-relaxed`}>{bio.backstory}</p>}
                      {bio.appearance && <BioRow label="Appearance" value={bio.appearance} />}
                      {bio.style && <BioRow label="Style" value={bio.style} />}
                    </div>
                  )}
                </div>
              )}
            </section>
          )}

          {/* CHARACTER SHEET + KNOWLEDGEBASE — the internal data-model (MBTI, trait/knowledge meters,
              reference corpus, skills) that drives personality diversification. Admin-only — the backend
              only ships `stats`/`knowledge`/`skills` to an admin, so a regular user never sees the knobs.
              Admins see the same detail here and in /admin (for tuning). */}
          {isAdmin && bio?.stats && (
            <section className="rounded-xl border border-secondary-200 dark:border-secondary-600 p-3">
              <p className={`${modalLabel} !mb-3`}>Character sheet <span className={`${TYPE.micro} text-secondary-400`}>(admin)</span></p>
              <AgentStatCard stats={bio.stats} />
            </section>
          )}

          {isAdmin && bio && (bio.knowledge.length > 0 || bio.skills.length > 0 || bio.capabilities.length > 0) && (
            <section>
              <p className={modalLabel}>Knowledgebase <span className={`${TYPE.micro} text-secondary-400`}>(admin)</span></p>
              {bio.knowledge.length > 0 && (
                <ul className="mt-1 space-y-1">
                  {bio.knowledge.map((k, i) => (
                    <li key={i} className={`flex items-start gap-1.5 ${TYPE.control} text-secondary-600 dark:text-secondary-300`}>
                      <BookOpen className="w-3.5 h-3.5 mt-0.5 shrink-0 text-secondary-400" /> {k}
                    </li>
                  ))}
                </ul>
              )}
              {(bio.skills.length > 0 || bio.capabilities.length > 0) && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {Array.from(new Set([...bio.skills, ...bio.capabilities])).map((s, i) => (
                    <span key={i} className={`rounded-full border border-secondary-200 dark:border-secondary-600 px-2 py-0.5 ${TYPE.meta} text-secondary-500 dark:text-secondary-400`}>{s}</span>
                  ))}
                </div>
              )}
            </section>
          )}

          {error && <p className={`${TYPE.body} text-secondary-500 dark:text-secondary-400`}>{error}</p>}

          {isOwner ? (
            <>
              {/* Display name (alias) */}
              <section>
                <p className={modalLabel}>Display name in this chat</p>
                <div className="flex gap-2">
                  <input value={alias} onChange={(e) => setAlias(e.target.value)} placeholder={member.name} maxLength={60} className={modalField} />
                  <button disabled={busy === 'alias'} onClick={saveAlias}
                    className={`shrink-0 rounded-lg bg-secondary-900 dark:bg-white px-3 py-2.5 ${TYPE.body} font-medium text-white dark:text-secondary-900 disabled:opacity-40`}>Save</button>
                </div>
              </section>

              {/* Per-group instructions — augments the agent's persona for THIS chat only (power-user) */}
              <section>
                <p className={modalLabel}>Extra instructions for this chat</p>
                <textarea value={ctx} onChange={(e) => setCtx(e.target.value)} rows={5} maxLength={2000}
                  placeholder={`Optional, owner-only — e.g. "focus on EU markets", "keep replies short", "we're building a fintech app". Adds to ${member.name}'s expertise for this group; doesn't change ${member.name} elsewhere.`}
                  className={`${modalField} min-h-[120px] resize-y`} />
                <div className="mt-2 flex items-center justify-between gap-3">
                  <p className={`${TYPE.control} text-secondary-400`}>Applies to this chat only — the template stays unchanged.</p>
                  <button disabled={busy === 'context' || ctx.trim() === (member.context || '')} onClick={saveContext}
                    className={`shrink-0 rounded-lg bg-secondary-900 dark:bg-white px-3 py-1.5 ${TYPE.control} font-medium text-white dark:text-secondary-900 disabled:opacity-40`}>
                    {busy === 'context' ? 'Saving…' : 'Save instructions'}
                  </button>
                </div>
              </section>

              {/* Knowledge base — for the owner's own custom agents (links + documents → RAG) */}
              {member.custom_agent_id && <AgentKnowledgeBase agentKey={member.custom_agent_id} onError={setError} />}
            </>
          ) : (
            <p className={`text-center ${TYPE.control} text-secondary-400`}>Only the group owner can rename this agent.</p>
          )}
        </div>
      </div>

      {/* Avatar lightbox — tap the profile image to see it larger. Backdrop or X closes. */}
      {lightbox && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/80 p-4" onClick={(e) => { e.stopPropagation(); setLightbox(null); }}>
          <img src={lightbox} alt={displayName} className="max-h-[88dvh] max-w-[88vw] rounded-2xl object-contain shadow-2xl" onClick={(e) => e.stopPropagation()} />
          <button onClick={(e) => { e.stopPropagation(); setLightbox(null); }} aria-label="Close image" title="Close"
            className="absolute top-4 right-4 rounded-full bg-black/40 p-1.5 text-white/80 hover:text-white hover:bg-black/60">
            <X className="h-5 w-5" />
          </button>
        </div>
      )}

      {(member.profile_id || member.custom_agent_id) && (
        <AgentGallery
          open={galleryOpen}
          asPage={!isDesktop}
          agentId={(member.profile_id || member.custom_agent_id) as string}
          agentName={displayName}
          initialTab={galleryTab}
          onClose={() => setGalleryOpen(false)}
          onWalletChanged={onWalletChanged}
        />
      )}
    </div>
  );
};

export default AgentDetailPanel;
